import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCQuery, invalidateQuery } from '../../Services/QueryCache'
import { FC } from '../../Services'
import ClientsTable from '../../Components/Tables/ClientsTable'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import BusinessIcon from '@mui/icons-material/Business'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'

export const AgencyDetails = () => {
  const loggedInUser = JSON.parse(window.localStorage.getItem('user')).user
  const agencyId = useParams()?.agencyId || loggedInUser.allAgencies?.[0]?._id || loggedInUser.agency?.agencyId || loggedInUser.agency?._id
  const { data: agency, isLoading } = useCQuery(['agency', agencyId], `/agencies/${agencyId}`)
  const { data: initialClients = [] } = useCQuery(['clientsByAgency', agencyId])
  const { data: configs = [], isSuccess: isSuccessConfigs } = useCQuery(['configs', { agencyId, isDeleted: false }])
  const [clients, setClients] = useState(initialClients)
  const navigate = useNavigate()
  const isSiapAdmin = ['siapAdmin', 'siapDeveloper'].includes(loggedInUser?.role)
  const basePath = loggedInUser?.role === 'siapDeveloper' ? '/siapdeveloper' : '/siapadmin'

  useEffect(() => {
    setClients(initialClients)
  }, [initialClients])

  const createdClients = clients.filter(
    (inst) => inst.status === 'created' || inst.status === 'running' || inst.status === 'offline'
  )

  const createNewClient = async () => {
    try {
      const agencyData = await FC.service('agencies').get(agencyId)

      if (!agencyData) throw new Error('Agenzia non trovata')

      const newClient = {
        agencyId: agencyData?._id,
        version: '',
        agencyName: agencyData?.name,
        status: 'created'
      }

      const savedClient = await FC.service('clients').create(newClient)
      setClients((prev) => [...prev, savedClient])

      invalidateQuery('clients')
      invalidateQuery('clientsByAgency')

      window.growl.show({
        severity: 'success',
        summary: 'Successo',
        detail: 'Il file e stato creato e salvato con successo.'
      })
    } catch (error) {
      console.error('Errore durante la creazione del client:', error)
      window.growl.show({
        severity: 'error',
        summary: 'Errore',
        detail: 'Il file non e stato creato! Riprovare.'
      })
    }
  }

  if (isLoading || !isSuccessConfigs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Stack alignItems='center' spacing={2}>
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary'>
            Caricamento dettagli agenzia...
          </Typography>
        </Stack>
      </Box>
    )
  }

  if (!agency) {
    return (
      <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1600, pt: 4 }}>
        <Paper
          elevation={0}
          sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}
        >
          <Typography variant='h6' color='text.secondary'>
            Agenzia non trovata.
          </Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1600, pb: 5 }}>
      {/* Page Header */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        sx={{ mb: 2, mt: 1, gap: 1.5 }}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <BusinessIcon color='primary' sx={{ fontSize: 28 }} />
          <Typography variant='h5' fontWeight={700} color='text.primary'>
            {agency.name || agency._id}
          </Typography>
          <Chip
            label={`ID: ${agency._id}`}
            size='small'
            variant='outlined'
            sx={{ fontWeight: 500 }}
          />
          <Chip
            icon={<DesktopWindowsIcon />}
            label={`${createdClients.length} client`}
            size='small'
            variant='outlined'
            sx={{ fontWeight: 500 }}
          />
        </Stack>

        <Stack direction='row' spacing={1.5}>
          {isSiapAdmin && (
            <Tooltip title='Torna alla lista agenzie'>
              <Button
                variant='outlined'
                size='small'
                onClick={() => navigate(`${basePath}/agencies`)}
                startIcon={<ArrowBackIcon />}
                aria-label='Torna alle agenzie'
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                Agenzie
              </Button>
            </Tooltip>
          )}
          <Tooltip title='Crea un nuovo client per questa agenzia'>
            <Button
              variant='contained'
              size='small'
              onClick={createNewClient}
              startIcon={<AddIcon />}
              aria-label='Crea nuovo client'
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Nuovo Client
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Clients Table */}
      <Paper
        elevation={0}
        sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1.5 }}>
          <DesktopWindowsIcon fontSize='small' color='action' />
          <Typography variant='body2' fontWeight={600} color='text.secondary'>
            Lista Client
          </Typography>
        </Stack>

        {createdClients.length > 0
          ? <ClientsTable configs={configs} clients={createdClients} />
          : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant='body1' color='text.secondary'>
                Nessun client trovato.
              </Typography>
              <Typography variant='body2' color='text.disabled' sx={{ mt: 0.5 }}>
                Crea un nuovo client con il pulsante sopra.
              </Typography>
            </Box>
            )}
      </Paper>
    </Box>
  )
}
